import React from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';
import {
    tableToolbarToggleGroupClass,
    tableToolbarToggleSegmentClass,
    tableToolbarIconClass,
} from '../lib/tableClasses';

export interface SegmentedToggleOption<T extends string> {
    value: T;
    /** Material Symbols icon name (e.g. `"view_kanban"`, `"view_list"`).
     *  Optional only with `showLabels`: an icon-only segment needs one. */
    icon?: IconName;
    /** Accessible label + tooltip for the segment. */
    label: string;
}

export interface SegmentedIconToggleProps<T extends string> {
    value: T;
    onChange: (value: T) => void;
    options: SegmentedToggleOption<T>[];
    /** Group-level aria-label describing the whole control. */
    ariaLabel: string;
    className?: string;
    /**
     * Write each option's `label` inside its segment (icon before it, if
     * any) instead of an icon-only square. For choices an icon can't name —
     * a period («7 días · 30 días · 12 meses»), a grouping, a unit.
     */
    showLabels?: boolean;
    /**
     * `primary` (default): the toolbar pill — bordered group, active segment
     * filled orange. `soft`: a grey track with the active segment in white,
     * for choosing WHAT TO SEE (a period, a range, an order) where orange
     * would compete with the page's actions (Financiero; Standards.md §20.2c).
     */
    appearance?: 'primary' | 'soft';
    /** `sm` (soft only): a tighter track for a control inside a card header. */
    size?: 'md' | 'sm';
}

const softGroup = 'inline-flex shrink-0 items-center gap-0.5 rounded-[10px] bg-slate-100 p-[3px] dark:bg-slate-800/80';
const softSegment = 'flex items-center gap-1.5 whitespace-nowrap rounded-lg font-display transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60';

/**
 * Icon-only segmented control — a pill group where exactly one segment is
 * active (filled primary) and the rest are muted. The canonical "view mode"
 * switch used in toolbars (kanban / table / list, grid / list, editor view…).
 *
 * Owns the shared DOM + class bundles (`tableToolbar*` from
 * `@shared/lib/tableClasses`) so the four view-toggles across tenant + admin
 * don't each re-implement them. Feature wrappers keep their own
 * mode-type, localStorage helpers and default logic, and just hand this
 * component an `options` array.
 *
 * For a **two-state sliding switch** with per-side colour use `BinaryToggle`.
 *
 * @example
 * <SegmentedIconToggle
 *   value={mode}
 *   onChange={setMode}
 *   ariaLabel="View mode"
 *   options={[
 *     { value: 'grid', icon: 'view_module', label: 'Cards' },
 *     { value: 'list', icon: 'view_list',   label: 'List' },
 *   ]}
 * />
 */
function SegmentedIconToggle<T extends string>({
    value,
    onChange,
    options,
    ariaLabel,
    className = '',
    showLabels = false,
    appearance = 'primary',
    size = 'md',
}: SegmentedIconToggleProps<T>): React.ReactElement {
    if (appearance === 'soft') {
        const pad = size === 'sm' ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs';
        return (
            <div className={`${softGroup} ${className}`.trim()} role="group" aria-label={ariaLabel}>
                {options.map((opt) => {
                    const active = value === opt.value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => onChange(opt.value)}
                            aria-pressed={active}
                            aria-label={showLabels ? undefined : opt.label}
                            title={showLabels ? undefined : opt.label}
                            className={`${softSegment} ${pad} ${
                                active
                                    ? 'bg-white font-semibold text-text-main shadow-sm dark:bg-surface-dark dark:text-white'
                                    : 'font-medium text-text-sub hover:text-text-main dark:text-slate-400 dark:hover:text-slate-200'
                            }`}
                        >
                            {opt.icon ? <Icon name={opt.icon} className="!text-[15px]" /> : null}
                            {showLabels || !opt.icon ? opt.label : null}
                        </button>
                    );
                })}
            </div>
        );
    }
    return (
        <div
            className={`${tableToolbarToggleGroupClass} ${className}`.trim()}
            role="group"
            aria-label={ariaLabel}
        >
            {options.map((opt) => {
                const active = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(opt.value)}
                        aria-pressed={active}
                        aria-label={showLabels ? undefined : opt.label}
                        title={showLabels ? undefined : opt.label}
                        className={`${tableToolbarToggleSegmentClass} ${
                            showLabels ? '!w-auto gap-1.5 px-3 text-xs font-semibold whitespace-nowrap' : ''
                        } ${
                            active
                                ? 'bg-primary text-white shadow-sm hover:brightness-110'
                                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100/90 dark:hover:bg-surface-hover-dark/80'
                        }`}
                    >
                        {opt.icon ? (
                            <Icon name={opt.icon} className={`${tableToolbarIconClass} ${active ? '!text-white' : ''}`} />
                        ) : null}
                        {showLabels ? opt.label : null}
                    </button>
                );
            })}
        </div>
    );
}

export default SegmentedIconToggle;
